import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const CUSTOMER_TOKEN_KEY = "rsi_customer_app_token";
const CUSTOMER_ACCESS_ID_KEY = "rsi_customer_access_id";

function readStorage(key) {
  if (typeof localStorage === "undefined") return "";
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeStorage(key, value) {
  if (typeof localStorage === "undefined") return;
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Ignore storage failures on restricted browsers.
  }
}

export default function useCustomerAppSession() {
  const [token, setToken] = useState(() => readStorage(CUSTOMER_TOKEN_KEY));
  const [lastAccessId, setLastAccessId] = useState(() => readStorage(CUSTOMER_ACCESS_ID_KEY));
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(Boolean(readStorage(CUSTOMER_TOKEN_KEY)));

  const clearSession = useCallback(() => {
    writeStorage(CUSTOMER_TOKEN_KEY, "");
    setToken("");
    setSession(null);
  }, []);

  const refresh = useCallback(
    async (nextToken = token) => {
      if (!nextToken) {
        setLoading(false);
        setSession(null);
        return null;
      }
      setLoading(true);
      try {
        const { data } = await api.get("/app/customer/me", {
          headers: { Authorization: `Bearer ${nextToken}` },
        });
        setSession(data);
        writeStorage(CUSTOMER_TOKEN_KEY, nextToken);
        setToken(nextToken);
        if (data?.customer?.customer_access_id) {
          writeStorage(CUSTOMER_ACCESS_ID_KEY, data.customer.customer_access_id);
          setLastAccessId(data.customer.customer_access_id);
        }
        return data;
      } catch {
        clearSession();
        return null;
      } finally {
        setLoading(false);
      }
    },
    [clearSession, token]
  );

  useEffect(() => {
    if (!token) return;
    refresh(token);
  }, [refresh, token]);

  const login = async (customerAccessId, accessCode) => {
    try {
      const { data } = await api.post("/app/customer/login", {
        customer_access_id: customerAccessId,
        access_code: accessCode,
      });
      const nextToken = data?.access_token || "";
      if (!nextToken) {
        clearSession();
        return { ok: false, error: "Customer app login failed. Missing session token." };
      }
      writeStorage(CUSTOMER_TOKEN_KEY, nextToken);
      writeStorage(CUSTOMER_ACCESS_ID_KEY, data?.customer?.customer_access_id || customerAccessId);
      setLastAccessId(data?.customer?.customer_access_id || customerAccessId);
      setToken(nextToken);
      setSession(data);
      return { ok: true, data };
    } catch (error) {
      clearSession();
      return { ok: false, error: formatApiError(error) };
    }
  };

  const logout = () => {
    clearSession();
  };

  return {
    session,
    loading,
    lastAccessId,
    login,
    logout,
    refresh,
    isAuthenticated: Boolean(session?.customer?.id && token),
  };
}
